import styled from '@emotion/styled'
import { ReactNode } from 'react'

import { ChatAvatar } from './ChatAvatar'
import { ChatBadge } from './ChatBadge'
import { CHAT_BROWSER_CONTROLS_MIN_HEIGHT } from './ChatBrowserControls'
import { ChatMembersHoverable } from './ChatMembersHoverable'
import { ChatModel } from './types'
import { Color, theme } from '../../constants'
import { Icon } from '../../elements'
import { useTranslations } from '../../translate'

const Actions = styled.div({
  display: 'flex',
  gap: theme.spacing[3.5],
})

const Header = styled.div({
  alignItems: 'center',
  backgroundColor: Color.WHITE,
  borderBottom: `${theme.border.width.thin}px solid ${Color.MEDIUM_GREY}`,
  boxShadow: theme.boxShadow.normal,
  display: 'flex',
  flexShrink: 0,
  justifyContent: 'space-between',
  // Reserve space for the box shadow:
  marginBottom: theme.spacing[2],
  minHeight: CHAT_BROWSER_CONTROLS_MIN_HEIGHT,
  paddingBottom: theme.spacing[3],
  paddingLeft: theme.spacing[4],
  paddingRight: theme.spacing[4],
  paddingTop: theme.spacing[3],
})

const Information = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[1.5],
})

const Location = styled.h2({
  alignItems: 'center',
  color: Color.DARKER_GREY,
  columnGap: theme.spacing[2],
  display: 'flex',
  fontSize: theme.font.size[6.5],
  fontWeight: theme.font.weight.medium,
  lineHeight: theme.lineHeight.normal,
  margin: 0,
  rowGap: theme.spacing[1],
})

const Partner = styled.h3({
  alignItems: 'center',
  color: Color.DARK_GREY,
  display: 'flex',
  gap: theme.spacing[2],
  fontSize: theme.font.size[6.5],
  fontWeight: theme.font.weight.book,
  lineHeight: theme.lineHeight.normal,
  margin: 0,
})

const Title = styled.h1<{ hasTitle: boolean }>((props) => ({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: theme.font.size[4],
  fontWeight: props.hasTitle ? theme.font.weight.bold : theme.font.weight.book,
  gap: theme.spacing[2.5],
  lineHeight: theme.lineHeight.normal,
  margin: 0,
}))

const WrappableContent = styled.div({
  alignItems: 'center',
  columnGap: 8,
  display: 'flex',
  flexWrap: 'wrap',
  rowGap: 2,
})

export type ChatHeaderProps = {
  actionsSlot?: ReactNode
  chat: Omit<ChatModel, 'id' | 'lastMessage'>
}

export const ChatHeader = ({ actionsSlot, chat }: ChatHeaderProps) => {
  const t = useTranslations()

  return (
    <Header>
      <Information>
        <Title hasTitle={!!chat.title}>
          {chat.isInternal && (
            <ChatBadge>{t.chat_view.internal_conversation()}</ChatBadge>
          )}
          {chat.title || `(${t.chat_view.no_subject()})`}
        </Title>
        <Location>
          {chat.location && (
            <Icon color={Color.DARKEST_MEDIUM_GREY} name="HomeIcon" round />
          )}
          <WrappableContent>
            {chat.location}
            {chat.badges?.map((props, idx) => (
              <ChatBadge key={idx} {...props} />
            ))}
          </WrappableContent>
        </Location>
        <Partner>
          <ChatAvatar
            image={chat.chatPartner.avatarUrl}
            isInternal={chat.isInternal}
            name={chat.chatPartner.name}
            size="xs"
          />
          <WrappableContent>
            {chat.chatPartner.name}
            {chat.chatPartner.badges?.map(ChatBadge)}
          </WrappableContent>
        </Partner>
      </Information>
      <Actions>
        {chat.members && <ChatMembersHoverable members={chat.members} />}
        {actionsSlot}
      </Actions>
    </Header>
  )
}
